import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ADMIN_API_URL } from '../../constants';
import { CREDIT_API_URL } from '../../constants';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import showToast from '../../utils/toast';

const leadStatuses = ["New", "Contacted", "Unable to connect", "Interested", "Awaiting Customer Details", "Awaiting Lender Update", "Approved", "Disbursed", "Rejected", "Dropped Off", "Invalid", "Did not Apply", "Loan Closed", "Loan Active", "EMI Overdue"];

const GenerateLeadPage = ({ addNotification }) => {
    const navigate = useNavigate();
    const { user } = useSelector((state) => state.auth);
    const [formData, setFormData] = useState({
        productType: '',
        customerName: '',
        email: '',
        mobile: '',
        loanAmount: '',
        source: '',
        assignedTo: '', // This will be fetched dynamically
        status: 'New', // Default status
        remarks: '',
        applicationType: 'Individual',
        panNumber: '',
        aadharNumber: '',
        documents: {
            panCard: '',
            aadharCard: '',
            bankStatement: '',
            itrStatement: ''
        }
    });
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const token = sessionStorage.getItem("authToken");
        if (!token) {
            addNotification("Authorization token not found. Please log in again.", 'error');
            navigate("/");
            return;
        }
        fetchUsers();
    }, [navigate]);

    const fetchUsers = async () => {
        const token = sessionStorage.getItem("authToken");
        if (!token) {
            addNotification("Authorization token not found. Please log in again.", 'error');
            return;
        }

        try {
            const response = await fetch(`${ADMIN_API_URL}users`, {
                method: "GET",
                headers: {
                    "Authorization": `${token}`,
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to fetch users.");
            }

            const result = await response.json();
            setUsers(result.data);
            // Don't preselect any user - let user choose manually for better UX
        } catch (err) {
            addNotification(`Error fetching users: ${err.message}`, 'error');
            setUsers([]);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: value
        }));
    };

    // Handle mobile number input with validation
    const handleMobileChange = (e) => {
        const { value } = e.target;

        // Remove all non-numeric characters
        const numericValue = value.replace(/\D/g, '');

        // Restrict to 10 digits maximum
        const restrictedValue = numericValue.slice(0, 10);

        // Update the form data
        setFormData(prevData => ({
            ...prevData,
            mobile: restrictedValue
        }));
    };

    // Validate mobile number format
    const validateMobileNumber = (mobile) => {
        // Check if mobile number is exactly 10 digits and starts with 6, 7, 8, or 9
        const mobileRegex = /^[6-9]\d{9}$/;
        return mobileRegex.test(mobile);
    };

    const handlePanChange = (e) => {
        let value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');

        // PAN format: 5 letters + 4 digits + 1 letter (ABCDE1234F)
        if (value.length <= 10) {
            // Ensure first 5 characters are letters
            if (value.length <= 5) {
                value = value.replace(/[^A-Z]/g, '');
            }
            // Ensure characters 6-9 are digits
            else if (value.length <= 9) {
                const letters = value.substring(0, 5).replace(/[^A-Z]/g, '');
                const digits = value.substring(5, 9).replace(/[^0-9]/g, '');
                value = letters + digits;
            }
            // Ensure last character is a letter
            else {
                const letters = value.substring(0, 5).replace(/[^A-Z]/g, '');
                const digits = value.substring(5, 9).replace(/[^0-9]/g, '');
                const lastLetter = value.substring(9, 10).replace(/[^A-Z]/g, '');
                value = letters + digits + lastLetter;
            }
        }

        setFormData(prevData => ({
            ...prevData,
            panNumber: value
        }));
    };

    const handleAadhaarChange = (e) => {
        let value = e.target.value.replace(/[^0-9]/g, ''); // Only digits

        // Format as XXXX XXXX XXXX for better readability
        if (value.length <= 12) {
            if (value.length > 8) {
                value = value.substring(0, 4) + ' ' + value.substring(4, 8) + ' ' + value.substring(8);
            } else if (value.length > 4) {
                value = value.substring(0, 4) + ' ' + value.substring(4);
            }
        }

        setFormData(prevData => ({
            ...prevData,
            aadharNumber: value
        }));
    };

    const handleDocumentChange = (e) => {
        const { name, files } = e.target;
        if (files.length > 0) {
            const file = files[0];
            // In a real application, you would upload this file to a server
            // For now, we'll just store the file name or a mock URL
            setFormData(prevData => ({
                ...prevData,
                documents: {
                    ...prevData.documents,
                    [name]: file.name // Storing file name as a placeholder
                }
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        // Validate mobile number before submission
        if (!validateMobileNumber(formData.mobile)) {
            showToast("Please enter a valid 10-digit mobile number starting with 6, 7, 8, or 9", 'error');
            addNotification("Please enter a valid mobile number", 'error');
            setLoading(false);
            return;
        }

        const token = sessionStorage.getItem("authToken");
        if (!token) {
            addNotification("Authorization token not found. Please log in again.", 'error');
            setLoading(false);
            return;
        }

        // Transform document paths into a more usable format for the API
        const documentsArray = Object.keys(formData.documents)
            .filter(docType => formData.documents[docType])
            .map(docType => ({
                documentType: docType.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()),
                documentPath: formData.documents[docType] // This would be the actual URL after upload
            }));

        const payload = {
            ...formData,
            mobile: formData.mobile ? Number(formData.mobile) : undefined,
            loanAmount: formData.loanAmount ? Number(formData.loanAmount) : undefined,
            assignedTo: formData.assignedTo, // Ensure assignedTo is the ID
            aadharNumber: formData.aadharNumber ? formData.aadharNumber.replace(/\s+/g, '') : '', // Remove spaces from Aadhaar
            documents: documentsArray,
            isConsentAccepted: true, // Default consent accepted
            createdBy: user?.id || user?.userId || null // Send user ID instead of name
        };
        // Remove document names as they are handled in documentsArray
        delete payload.documents.panCard;
        delete payload.documents.aadharCard;
        delete payload.documents.bankStatement;
        delete payload.documents.itrStatement;


        try {
            const response = await fetch(`${CREDIT_API_URL}create-lead`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `${token}`,
                },
                body: JSON.stringify(payload),
            });

            const result = await response.json();

            if (response.ok) {
                // Success case (status 200)
                showToast("New lead generated successfully!", 'success');
                addNotification("New lead generated successfully.", 'success');
                navigate("/leads"); // Navigate back to leads page
            } else if (response.status === 400) {
                // Mobile not verified case
                if (result.message && result.message.includes("Mobile is not verified")) {
                    showToast(`Lead exists but mobile not verified: ${result.data?.mobile || 'Unknown mobile'}`, 'warning');
                    addNotification(result.message, 'warning');
                } else {
                    showToast(result.message || "Failed to generate lead", 'error');
                    addNotification(result.message || "Failed to generate lead", 'error');
                }
            } else if (response.status === 409) {
                // Lead already exists case
                showToast(`Lead already exists for this product type: ${result.data?.id || 'Unknown ID'}`, 'info');
                addNotification(result.message || "Lead already exists", 'info');
            } else {
                // Other error cases
                showToast(result.message || "Failed to generate lead", 'error');
                addNotification(result.message || "Failed to generate lead", 'error');
            }
        } catch (err) {
            showToast(`Network error: ${err.message}`, 'error');
            addNotification(`Error generating lead: ${err.message}`, 'error');
            console.error("Error generating lead:", err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Header />
            <div id="wrapper" style={{ paddingTop: '70px' }}>
                <div className="content">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="hpanel">
                                <div className="panel-body">
                                    <h2 className="font-light m-b-xs">Generate New Lead</h2>
                                </div>
                            </div>
                            <div className="hpanel">
                                <div className="panel-body">
                                    <form onSubmit={handleSubmit}>
                                        <div className="form-group">
                                            <label>Product Type *</label>
                                            <select className="form-control" name="productType" value={formData.productType} onChange={handleChange} required>
                                                <option value="">Select Product Type</option>
                                                <option value="Personal Loan">Personal Loan</option>
                                                <option value="Business Loan">Business Loan</option>
                                                <option value="Home Loan">Home Loan</option>
                                                <option value="COMMERCIAL VEHICLE LOAN">Commercial Vehicle Loan</option>
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>Customer Name</label>
                                            <input type="text" className="form-control" name="customerName" value={formData.customerName} onChange={handleChange} autoComplete="off" />
                                        </div>
                                        <div className="form-group">
                                            <label>Email</label>
                                            <input type="email" className="form-control" name="email" value={formData.email} onChange={handleChange} autoComplete="off" />
                                        </div>
                                        <div className="form-group">
                                            <label>Mobile *</label>
                                            <input
                                                type="tel"
                                                className={`form-control ${formData.mobile && !validateMobileNumber(formData.mobile) ? 'is-invalid' : ''}`}
                                                name="mobile"
                                                value={formData.mobile}
                                                onChange={handleMobileChange}
                                                placeholder="Enter 10-digit mobile number"
                                                pattern="[6-9][0-9]{9}"
                                                title="Mobile number must be 10 digits and start with 6, 7, 8, or 9"
                                                required
                                                autoComplete="off"
                                            />
                                            {formData.mobile && !validateMobileNumber(formData.mobile) && (
                                                <div className="invalid-feedback" style={{ display: 'block' }}>
                                                    Mobile number must be 10 digits and start with 6, 7, 8, or 9
                                                </div>
                                            )}
                                        </div>
                                        <div className="form-group">
                                            <label>Loan Amount *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="loanAmount"
                                                value={formData.loanAmount}
                                                onChange={(e) => {
                                                    // Only allow numeric input
                                                    const value = e.target.value.replace(/[^0-9]/g, '');
                                                    setFormData(prevData => ({
                                                        ...prevData,
                                                        loanAmount: value
                                                    }));
                                                }}
                                                required
                                                autoComplete="off"
                                                placeholder="Enter loan amount"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Source</label>
                                            <select className="form-control" name="source" value={formData.source} onChange={handleChange}>
                                                <option value="">Select Source</option>
                                                <option value="Direct">Direct</option>
                                                <option value="Direct Lender Marketing">Direct Lender Marketing</option>
                                                <option value="Whatsapp">Whatsapp</option>
                                                <option value="Self">Self</option>
                                                <option value="Social Media">Social Media</option>
                                                <option value="Groups">Groups</option>
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>Assigned To</label>
                                            <select className="form-control" name="assignedTo" value={formData.assignedTo} onChange={handleChange}>
                                                <option value="">Select User</option>
                                                {users.filter(u => u.userType === 0).map(user => <option key={user.id} value={user.id}>{user.name}</option>)}
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>Status</label>
                                            <select className="form-control" name="status" value={formData.status} onChange={handleChange}>
                                                {leadStatuses.map(status => <option key={status} value={status}>{status}</option>)}
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>Remarks</label>
                                            <textarea className="form-control" name="remarks" value={formData.remarks} onChange={handleChange} rows="3" autoComplete="off"></textarea>
                                        </div>

                                        <hr />
                                        <h5>Loan Application Details</h5>

                                        <div className="form-group">
                                            <label>Application Type *</label>
                                            <select className="form-control" name="applicationType" value={formData.applicationType} onChange={handleChange} required>
                                                <option value="Individual">Individual</option>
                                                <option value="Company">Company</option>
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>PAN Number</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="panNumber"
                                                value={formData.panNumber}
                                                onChange={handlePanChange}
                                                maxLength="10"
                                                placeholder="ABCDE1234F"
                                                autoComplete="off"
                                                style={{ textTransform: 'uppercase' }}
                                            />
                                            <small className="text-muted">Format: 5 letters + 4 digits + 1 letter (e.g., ABCDE1234F)</small>
                                        </div>
                                        <div className="form-group">
                                            <label>Aadhaar Number</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="aadharNumber"
                                                value={formData.aadharNumber}
                                                onChange={handleAadhaarChange}
                                                maxLength="14"
                                                placeholder="1234 5678 9012"
                                                autoComplete="off"
                                            />
                                            <small className="text-muted">Enter 12-digit Aadhaar number</small>
                                        </div>

                                        <h6>Documents (Select from Computer)</h6>
                                        <div className="form-group">
                                            <label>PAN Card Document</label>
                                            <input type="file" className="form-control" name="panCard" onChange={handleDocumentChange} />
                                            {formData.documents.panCard && <p className="text-muted">Selected: {formData.documents.panCard}</p>}
                                        </div>
                                        <div className="form-group">
                                            <label>Aadhar Card Document</label>
                                            <input type="file" className="form-control" name="aadharCard" onChange={handleDocumentChange} />
                                            {formData.documents.aadharCard && <p className="text-muted">Selected: {formData.documents.aadharCard}</p>}
                                        </div>
                                        <div className="form-group">
                                            <label>Bank Statement Document</label>
                                            <input type="file" className="form-control" name="bankStatement" onChange={handleDocumentChange} />
                                            {formData.documents.bankStatement && <p className="text-muted">Selected: {formData.documents.bankStatement}</p>}
                                        </div>
                                        <div className="form-group">
                                            <label>ITR Statement Document</label>
                                            <input type="file" className="form-control" name="itrStatement" onChange={handleDocumentChange} />
                                            {formData.documents.itrStatement && <p className="text-muted">Selected: {formData.documents.itrStatement}</p>}
                                        </div>
                                        <div className="text-right">
                                            <button type="button" className="btn btn-default" onClick={() => navigate('/leads')}>Cancel</button>
                                            <button type="submit" className="btn btn-primary m-l-sm" disabled={loading}>Generate Lead</button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
            </div>
        </>
    );
};

export default GenerateLeadPage; 